/** Channel Lineup — the channel ⇄ package graph. */

import type {
  ChannelListResponse,
  ChannelPackages,
  ChannelResponse,
  LocalStation,
  PackageRef
} from "../types/channels-service";
import { combineTunerPositions } from "./tuner-positions";

/** A channel's relationship to ONE package. Mirrors the organism's own
 *  `Membership` (`channel-lineup.types.ts`); `resolve-lineup-for.ts` is the
 *  seam where the compiler proves the two still agree. */
export type Membership = "included" | "addon" | "promo";

/**
 * Which shelf a package sits on, in the model's vocabulary. The model's name is singular, the
 * wire's is plural, and the two are joined only by `WIRE_BUCKETS` below.
 */
export type LineupBucket = "signature" | "genre" | "addon" | "genreAddon";

/**
 * Whether a channel is purchasable as an add-on at all, and on which terms — a per-channel fact,
 * independent of any one package. This is what production's `$` and `★` are actually built from,
 * and it is the single thing the v3 model had no room for.
 */
export type AddonReach = "addon" | "promo";

/** `LineupModel.platform` — the two feeds this component is built on. */
export type LineupPlatform = "ott" | "satellite";

const PLATFORMS: Record<string, LineupPlatform> = {
  ott: "ott",
  satellite: "satellite"
};

/**
 * An authored `platform` scalar -> the feed its codes are looked up in. Anything unrecognised reads
 * as `"ott"`, the default `buildSignatureSections` already has, so a typo renders the internet
 * lineup rather than nothing.
 */
export function toLineupPlatform(value: unknown): LineupPlatform {
  return PLATFORMS[String(value ?? "").trim().toLowerCase()] ?? "ott";
}

/** Wire bucket name -> model bucket. The ONLY place the plural is spelled. */
const WIRE_BUCKETS: ReadonlyArray<readonly [keyof ChannelPackages, LineupBucket]> = [
  ["signature", "signature"],
  ["genre", "genre"],
  ["addons", "addon"],
  ["genreAddons", "genreAddon"]
];

/** Which bucket wins when one code appears in two of them. */
const BUCKET_RANK: Record<LineupBucket, number> = {
  signature: 0,
  genre: 1,
  addon: 2,
  genreAddon: 3
};

/**
 * Which membership wins when one code resolves twice for one channel (the dual-bucket codes above,
 * whose two refs can disagree on `isPromo`).
 */
const MEMBERSHIP_RANK: Record<Membership, number> = {
  included: 0,
  promo: 1,
  addon: 2
};

/** One package, as the model knows it. Identity is the code — never the name. */
export interface LineupPackage {
  /** Identity. `PackageRef.billingProductCode`. */
  billingProductCode: string;
  /**
   * Channels-service / Epoch display name. Fallback label only (§2) — presentation comes from
   * package-config looked up by code, and this is what renders when G10's three un-authored rows
   * have no entry.
   */
  name: string;
  bucket: LineupBucket;
  /** CCIDs, bucketed by their relationship to THIS package. */
  channelIds: Record<Membership, string[]>;
}

/** One channel, as the model knows it (DATA-CONTRACT §2). */
export interface LineupChannel {
  /** CCID. Opaque identity. */
  id: string;
  name: string;
  number?: string;
  callSign?: string;
  /**
   * The resolved choice, not the feed's field of the same name — OV's `imageURL` wins and the
   * feed's `logoUrl` is the backup (`pickLineupLogo`). It keeps the name because that is the
   * organism prop it feeds.
   */
  logoUrl?: string;
  /** channels-service `categories[]`, verbatim. */
  categories: string[];
  /** `"rsn-oom"` comes from the feed's `channelType`; `"rsn"` is mapped from beam at the ZIP seam
   *  (G1) and never originates here. */
  kind: "national" | "local" | "rsn-oom" | "rsn";
  /**
   * In-market RSNs only — GR5's tier, mapped from beam's all-lowercase `rsntier` in
   * `lineup-market.ts`. Never set from the feed: channels-service excludes RSNs from `/ott` and
   * `/satellite` on purpose.
   */
  tier?: number;
  /**
   * In-market RSNs only — GR4's team attribution, unioned from beam's `mlb` / `nhl` / `nba` /
   * `wnba` flags.
   */
  teams?: string[];
  /** In-market RSNs only — beam's `packageNames` (plan display names). The
   *  resolver matches them to the signature columns' code segment for GR2's ✓;
   *  `packages` stays `{}` because beam carries no billing codes. */
  beamPackageNames?: string[];
  myFree: boolean;
  fastChannel: boolean;
  /** `billingProductCode` -> this channel's relationship to that package. */
  packages: Record<string, Membership>;
  /**
   * GR2 — this channel's add-on reach, from its `addons` refs alone. Absent on a channel with none,
   * which is what makes the projection a no-op there. See `AddonReach`; `resolve-lineup-for.ts` is
   * the only reader.
   */
  addonReach?: AddonReach;
}

/** The graph, one per platform, cached per populate cycle. */
export interface LineupModel {
  platform: LineupPlatform;
  /** The feed's own `lastUpdated`. Doubles as the refresh stamp the resolver
   *  dedupes its drop logging on. */
  lastUpdated: string;
  /** Keyed by `billingProductCode`. */
  packages: Record<string, LineupPackage>;
  /** Keyed by CCID. Nationals only — locals live on their own key (§2). */
  channels: Record<string, LineupChannel>;
  /**
   * Refs the feed sent with no usable `billingProductCode`. G7 says a ref without a code is a
   * defect: dropped, never aliased by name. Counted so a regression in feed coverage is visible
   * without re-auditing by hand.
   */
  refsWithoutCode: number;
}

/** Per-market locals, the model's shape. One cache key, sliced per request —
 *  see the populator for why this is not ~199 keys per platform. */
export type LineupLocalsByDma = Record<string, LineupChannel[]>;

/** A shaped-but-empty model. */
export function emptyLineupModel(platform: LineupPlatform): LineupModel {
  return {
    platform,
    lastUpdated: "",
    packages: {},
    channels: {},
    refsWithoutCode: 0
  };
}

function isUsableCode(code: unknown): code is string {
  return typeof code === "string" && code.trim() !== "";
}

/**
 * Filenames the channels-service puts in a logo field when it has no artwork for that channel. Two,
 * and both are live.
 */
const NO_LOGO_MARKERS = ["channel-logo_missing-logo", "Channel_Icon_No-Logo"];

/**
 * The logo choice for this component: OV's catalog `imageURL` first, the service's own `logoUrl` as
 * the backup, as the tech lead decided. No flag.
 */
function pickLineupLogo(source: ChannelResponse | LocalStation): string | undefined {
  const ovLogo = (source.imageURL ?? "").trim();
  if (ovLogo !== "" && !NO_LOGO_MARKERS.some(marker => ovLogo.includes(marker))) {
    return ovLogo;
  }
  // The last arm is `logoUrl` verbatim — a placeholder picture beats no
  // picture, and nothing below this has another source to try.
  return (source.logoUrl ?? "").trim() || undefined;
}

/**
 * A ref's membership in its own package. `isPromo` wins over the bucket's default because a
 * roll-to-pay benefit is a different cell from a plain add-on (GR2).
 */
function membershipOf(ref: PackageRef, bucket: LineupBucket): Membership {
  if (ref.isPromo === true) {
    return "promo";
  }
  return bucket === "signature" || bucket === "genre" ? "included" : "addon";
}

/**
 * The stronger of two claims on one package, by `MEMBERSHIP_RANK`. Exported for the seam, which
 * projects `AddonReach` onto the authored columns and must break an `included`-vs-`addon` tie the
 * same way this file breaks it — a real `included` wins.
 */
export function stronger(a: Membership | undefined, b: Membership): Membership {
  if (!a) {
    return b;
  }
  return MEMBERSHIP_RANK[b] < MEMBERSHIP_RANK[a] ? b : a;
}

/**
 * Plan display name -> `billingProductCode`, built from the feed's own `included` buckets — the one
 * join `addons[].includedWith` needs (G5). This is not the name matching §1 forbids, and the
 * distinction is the whole reason it is allowed here.
 */
function planCodesByName(
  sources: ReadonlyArray<ChannelResponse | LocalStation | undefined>
): Map<string, string> {
  const byName = new Map<string, string>();
  for (const bucket of ["signature", "genre"] as const) {
    for (const source of sources) {
      for (const ref of source?.packages?.[bucket] ?? []) {
        const name = (ref?.name ?? "").trim();
        if (name !== "" && isUsableCode(ref?.billingProductCode) && !byName.has(name)) {
          byName.set(name, ref.billingProductCode as string);
        }
      }
    }
  }
  return byName;
}

/**
 * G5 — the plans a ref says it is bundled into, as codes. It resolves to `included`, not to
 * `addon`.
 */
function includedWithCodes(ref: PackageRef, byName: Map<string, string>): string[] {
  const raw: unknown = ref?.includedWith;
  if (!Array.isArray(raw)) {
    return [];
  }
  const codes: string[] = [];
  for (const entry of raw) {
    const code = byName.get(typeof entry === "string" ? entry.trim() : "");
    if (code) {
      codes.push(code);
    }
  }
  return codes;
}

/**
 * A national channel's display name: `displayName ?? name`, minus a trailing `-<callSign>` anchored
 * on the channel's own call sign (never a split on "-", which would truncate `C-SPAN2`, `RFD-TV`,
 * `Yu-Gi-Oh!`).
 */
function displayNameOf(source: ChannelResponse | LocalStation): string {
  // `displayName` (the consumer-facing Storyblok/Mongo override) wins over the
  // v4 internal `name` — the same precedence the feed's other consumer applies
  // (`channel-listing-adapter.ts:394-395`), read there, not imported.
  const name = (source.displayName ?? source.name ?? "").trim();
  const callSign = (source.callSign ?? "").trim();
  if (callSign === "") {
    return name;
  }
  const suffix = `-${callSign}`;
  if (!name.endsWith(suffix)) {
    return name;
  }
  // Never to nothing: a station whose whole name IS its suffix keeps the name,
  // because an empty label has no accessible name and no sort position.
  return name.slice(0, -suffix.length).trim() || name;
}

/** Map one feed channel to the model's shape. Builds a new object, always, and copies `categories`. */
/** A local station's name, as production renders it. */
function localDisplayNameOf(source: ChannelResponse | LocalStation): string {
  const network = (source.networkAffiliates?.[0] ?? "").trim();
  return network || displayNameOf(source);
}

/* The shared `ChannelResponse` type stays as v2's adapter expects it, so the feed's `channelType`
   is read through this guard rather than declared on the shared type. */
function hasChannelType(source: object): source is { channelType?: unknown } {
  return "channelType" in source;
}

/** The feed tags out-of-market RSNs `channelType: "rsn-oom"` (WAT-12363); every other row is national. */
function feedKindOf(source: ChannelResponse): LineupChannel["kind"] {
  return hasChannelType(source) && source.channelType === "rsn-oom" ? "rsn-oom" : "national";
}

function toLineupChannel(
  source: ChannelResponse | LocalStation,
  kind: LineupChannel["kind"]
): LineupChannel {
  return {
    id: source.id,
    name: kind === "local" ? localDisplayNameOf(source) : displayNameOf(source),
    number: source.number,
    callSign: source.callSign,
    logoUrl: pickLineupLogo(source),
    categories: [...(source.categories ?? [])],
    kind,
    myFree: source.myFree === true,
    fastChannel: source.fastChannel === true,
    packages: {}
  };
}

/**
 * Fold one channel's package refs into both halves of the graph. Returns how many refs were dropped
 * for want of a code (G7).
 */
function indexChannel(
  channel: LineupChannel,
  wire: ChannelPackages | undefined,
  packages: Record<string, LineupPackage>,
  byName: Map<string, string>
): number {
  let dropped = 0;
  for (const [wireKey, bucket] of WIRE_BUCKETS) {
    for (const ref of wire?.[wireKey] ?? []) {
      const code = ref?.billingProductCode;
      if (!isUsableCode(code)) {
        // G7 — a ref with no code is a defect: drop it, never alias by name.
        dropped += 1;
        continue;
      }
      const membership = membershipOf(ref, bucket);

      /* GR2 — the channel's add-on reach, from the `addons` bucket alone. */
      if (wireKey === "addons") {
        channel.addonReach =
          ref.isPromo === true || channel.addonReach === "promo" ? "promo" : "addon";
      }

      /* G5 — the plans this ref is bundled into are `included`, resolved to codes by the feed's own
         names. */
      for (const bundled of includedWithCodes(ref, byName)) {
        channel.packages[bundled] = stronger(channel.packages[bundled], "included");
      }

      const existing = packages[code];
      if (!existing) {
        packages[code] = {
          billingProductCode: code,
          name: ref.name ?? "",
          bucket,
          channelIds: {
            included: [],
            addon: [],
            promo: []
          }
        };
      } else if (BUCKET_RANK[bucket] < BUCKET_RANK[existing.bucket]) {
        existing.bucket = bucket;
      }

      channel.packages[code] = stronger(channel.packages[code], membership);
    }
  }
  return dropped;
}

/** Folds a second tuner position of one channel into the kept row: memberships take the stronger
 *  claim, the rest unions. */
function mergeTunerPosition(kept: LineupChannel, other: LineupChannel): void {
  for (const [code, membership] of Object.entries(other.packages)) {
    kept.packages[code] = stronger(kept.packages[code], membership);
  }
  if (other.addonReach) {
    kept.addonReach = kept.addonReach === "promo" || other.addonReach === "promo" ? "promo" : "addon";
  }
  kept.categories = [...new Set([...kept.categories, ...other.categories])];
  kept.myFree = kept.myFree || other.myFree;
  kept.fastChannel = kept.fastChannel || other.fastChannel;
  kept.logoUrl = kept.logoUrl ?? other.logoUrl;
}

/** Second pass: a channel's FINAL membership per code decides which list it
 *  joins, so a code found in two buckets cannot land one channel twice. */
function fillChannelIds(
  channel: LineupChannel,
  packages: Record<string, LineupPackage>
): void {
  for (const [code, membership] of Object.entries(channel.packages)) {
    packages[code]?.channelIds[membership].push(channel.id);
  }
}

/**
 * Build the model for one platform from the raw feed envelope. May throw — and its caller must
 * isolate it.
 */
export function buildLineupModel(
  raw: ChannelListResponse,
  platform: LineupPlatform
): LineupModel {
  const packages: Record<string, LineupPackage> = {};
  const channels: Record<string, LineupChannel> = {};
  let refsWithoutCode = 0;

  // Built over the WHOLE feed first: a channel's `includedWith` names a plan
  // that any other channel may be the one to state the code for.
  const byName = planCodesByName(raw?.channels ?? []);
  const groups = new Map<string, { channel: LineupChannel; numbers: string[] }>();

  for (const source of raw?.channels ?? []) {
    if (!source?.id) {
      continue;
    }
    const channel = toLineupChannel(source, feedKindOf(source));
    refsWithoutCode += indexChannel(
      channel,
      source.packages,
      packages,
      byName
    );
    /* The feed sends one row per tuner position; one logical channel is one row, as in v2's
       `dedupeChannelsByCallSign`. No call sign, no reliable key, so no grouping. */
    const callSign = (channel.callSign ?? "").trim();
    const key = callSign ? `${callSign}|${channel.name}` : "";
    const group = key ? groups.get(key) : undefined;
    if (group) {
      mergeTunerPosition(group.channel, channel);
      group.numbers.push(channel.number ?? "");
      continue;
    }
    if (key) {
      groups.set(key, {
        channel,
        numbers: [channel.number ?? ""]
      });
    }
    channels[channel.id] = channel;
  }

  for (const { channel, numbers } of groups.values()) {
    if (numbers.length > 1) {
      channel.number = combineTunerPositions(numbers);
    }
  }

  for (const channel of Object.values(channels)) {
    fillChannelIds(channel, packages);
  }

  return {
    platform,
    lastUpdated: raw?.lastUpdated ?? "",
    packages,
    channels,
    refsWithoutCode
  };
}

/** Build the per-market locals slice from the same envelope. */
export function buildLineupLocals(raw: ChannelListResponse): LineupLocalsByDma {
  const byDma: LineupLocalsByDma = {};
  /* The same plan vocabulary the nationals resolved against, so one station cannot read
     `includedWith` differently from the channel beside it in the grid. */
  const byName = planCodesByName([
    ...(raw?.channels ?? []),
    ...Object.values(raw?.locals?.byDma ?? {}).flat()
  ]);
  for (const [dma, stations] of Object.entries(raw?.locals?.byDma ?? {})) {
    const scratch: Record<string, LineupPackage> = {};
    const mapped: LineupChannel[] = [];
    for (const station of stations ?? []) {
      if (!station?.id) {
        continue;
      }
      const channel = toLineupChannel(station, "local");
      indexChannel(channel, station.packages, scratch, byName);
      mapped.push(channel);
    }
    byDma[dma] = mapped;
  }
  return byDma;
}
